import { useEffect } from 'react';
import { useCauseList } from '../hooks/useCauseList';

function formatDate(yyyymmdd: string): string {
  const y = yyyymmdd.slice(0, 4);
  const m = parseInt(yyyymmdd.slice(4, 6), 10) - 1;
  const d = parseInt(yyyymmdd.slice(6, 8), 10);
  return new Date(parseInt(y), m, d).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
}

const FAQS: { q: string; a: string }[] = [
  {
    q: "What is a Madras High Court cause list?",
    a: "A cause list is the official daily schedule published by the Madras High Court showing which cases each court will hear, in what order (item number), and before which judge. It is usually published the evening before the working day."
  },
  {
    q: "How do I check today's Madras High Court cause list?",
    a: 'Open the Cause List tab above, choose Madras (Principal Seat, Chennai) or Madurai Bench, and browse case-by-case under each court, or use the search bar to jump straight to a case number or party name.'
  },
  {
    q: 'What is the difference between the Cause List and the Live Board?',
    a: "The cause list is the full day's schedule prepared the evening before. The Live Board shows the item number and case each court is actually hearing right now, refreshed every couple of minutes while the court is sitting."
  },
  {
    q: 'Can I search by case number or party name?',
    a: 'Yes. The search bar in the Cause List tab matches case numbers (for example WP/12345/2026) as well as petitioner and respondent names across every court in the list.'
  },
  {
    q: 'What does the item number mean?',
    a: "The item number is a case's position in a court's daily list. Courts work through items roughly in order, so a case at item 45 is typically called only after the cases before it are disposed of, adjourned, or passed over."
  },
  {
    q: 'Can I get notified when my case is coming up?',
    a: 'Yes — tap the bell icon next to a case to enable a notification when the court reaches a case within a few items of it. Notifications are set per case, per day.'
  },
  {
    q: 'Is video conferencing (VC) hearing information available?',
    a: 'Courts conducting hybrid or video-conference hearings are marked with a VC badge on the Live Board. Open a court card to view its VC link when one is available.'
  },
  {
    q: 'What happens to the cause list on weekends and court holidays?',
    a: 'The Madras High Court does not sit on Saturdays, Sundays, or notified holidays, so no new cause list or live board data is generated on those days. Court Pulse continues to show the most recently published list until the next working day.'
  },
  {
    q: 'Is Court Pulse an official Madras High Court website?',
    a: 'No. Court Pulse is an independent, unofficial tool built to make the publicly available cause list and display board easier to browse and search. For authoritative case information, always refer to the official Madras High Court website.'
  },
  {
    q: 'What do tags like "ADMISSION" or "FINAL HEARING" mean on a court card?',
    a: "These tags show the hearing type or category noted against the case in the cause list — for example whether it's up for admission, a final hearing, or being mentioned before the court that day. The label comes directly from the official cause list entry for that case."
  },
  {
    q: 'What is the Daily Orders tab?',
    a: "Daily Orders lets you browse the orders a court has passed on a given working day, with a direct link to each order's official PDF. Pick a date and search by case number to find a specific order."
  },
  {
    q: 'Can I download the official cause list as a PDF?',
    a: "Yes. On the Cause List tab, tap the download icon next to the tab title to open that day's official cause list PDF from the Madras High Court website for the selected bench."
  },
  {
    q: 'Can I install Court Pulse on my phone like an app?',
    a: 'Yes. Court Pulse is a Progressive Web App — on Android Chrome you\'ll get an "Add to Home Screen" install prompt, and on iOS Safari you can use Share → Add to Home Screen. Once installed it opens full-screen like a native app.'
  },
  {
    q: 'Why does a court sometimes show "LIVE" with a very large elapsed time?',
    a: "The elapsed time counts from when a court's current case was last updated. If the display board doesn't explicitly mark a court \"list over,\" its last case can otherwise keep showing as live for many hours. Court Pulse resets every court to a fresh, idle state at midnight IST each day to keep this accurate."
  }
];

export function CauseListSeoBlock() {
  const madras = useCauseList('madras');
  const madurai = useCauseList('madurai');

  useEffect(() => {
    if (madras.meta?.date) {
      document.title = `Madras High Court Cause List ${formatDate(madras.meta.date)} | Court Pulse`;
    }
  }, [madras.meta?.date]);

  const summaryLines = [
    madras.meta?.date && madras.meta?.totalCases
      ? `Madras (Principal Seat) — ${formatDate(madras.meta.date)}: ${madras.meta.totalCases} cases across ${madras.meta.totalCourts} courts.`
      : '',
    madurai.meta?.date && madurai.meta?.totalCases
      ? `Madurai Bench — ${formatDate(madurai.meta.date)}: ${madurai.meta.totalCases} cases across ${madurai.meta.totalCourts} courts.`
      : '',
  ].filter(Boolean);

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <section className="seo-block" aria-label="Cause list frequently asked questions">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      <div className="seo-block__header">
        <div className="seo-block__header-left">
          <span className="seo-block__icon">❓</span>
          <div>
            <h2 className="seo-block__title">FREQUENTLY ASKED QUESTIONS</h2>
            <p className="seo-block__subtitle">Madras High Court cause list &amp; live board — how it works.</p>
          </div>
        </div>
      </div>

      {summaryLines.length > 0 && (
        <div className="seo-block__summary">
          {summaryLines.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      )}

      <div className="seo-block__faq-list">
        {FAQS.map((f) => (
          <details key={f.q} className="seo-block__faq-item">
            <summary className="seo-block__faq-q"><h3>{f.q}</h3></summary>
            <p className="seo-block__faq-a">{f.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}
